import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LANGS } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Globe } from "lucide-react";
import api from "@/lib/api";

// Server messages translated on display: t("Email ou mot de passe incorrect") t("Trop de tentatives. Réessayez dans 15 minutes.") t("Lien invalide ou expiré. Refaites une demande de mot de passe.") t("Lien déjà utilisé.")

export const LoginLangPicker = () => {
  const { lang, changeLang } = useI18n();
  return (
    <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
      <Globe className="w-4 h-4" />
      <select value={lang} onChange={(e) => changeLang(e.target.value)} data-testid="login-lang"
        className="bg-transparent border border-input rounded-md h-8 px-2 text-sm text-foreground">
        {LANGS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
      </select>
    </label>
  );
};

const ForgotForm = ({ initialEmail, onBack }) => {
  const { t } = useI18n();
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(t(formatApiErrorDetail(err.response?.data?.detail) || err.message)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      {sent ? (
        <p className="text-sm rounded-md border border-[#9E7B41]/40 bg-[#9E7B41]/10 p-3" data-testid="forgot-sent">
          {t("Si un compte existe pour cette adresse, un e-mail vient de vous être envoyé avec un lien pour choisir votre mot de passe (valable 1 heure). Pensez à regarder dans vos courriers indésirables.")}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t("Indiquez votre e-mail : vous recevrez un lien pour choisir votre mot de passe.")}</p>
          <div><Label htmlFor="f-email">{t("Email")}</Label>
            <Input id="f-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="forgot-email" /></div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Recevoir le lien")}
          </Button>
        </>
      )}
      <button type="button" onClick={onBack} className="block w-full text-center text-sm text-muted-foreground underline" data-testid="forgot-back">{t("Retour à la connexion")}</button>
    </form>
  );
};

const LoginForm = ({ email, setEmail, onForgot }) => {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const from = typeof loc.state?.from === "string" && loc.state.from.startsWith("/") && !loc.state.from.startsWith("//") ? loc.state.from : "/";
  const [loading, setLoading] = useState(false);
  const [pass, setPass] = useState("");
  const [show, setShow] = useState(false);
  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, pass);
      toast.success(t("Connexion réussie"));
      nav(from, { replace: true });
    } catch (err) {
      const st = err.response?.status;
      if (!err.response) toast.error(`${t("Serveur injoignable, vérifiez votre connexion internet et réessayez.")} (${err.code || "réseau"})`);
      else if (!err.response.data?.detail) toast.error(`${t("Une erreur est survenue. Veuillez réessayer.")} (${st})`);
      else toast.error(t(formatApiErrorDetail(err.response.data.detail)));
    } finally { setLoading(false); }
  };
  return (
    <form onSubmit={doLogin} className="space-y-4">
      <div>
        <Label htmlFor="l-email">{t("Email")}</Label>
        <Input id="l-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
          placeholder={t("vous@exemple.com")} className="mt-1.5" />
      </div>
      <div>
        <Label htmlFor="l-pass">{t("Mot de passe")}</Label>
        <div className="relative mt-1.5">
          <Input id="l-pass" type={show ? "text" : "password"} required value={pass} onChange={(e) => setPass(e.target.value)}
            data-testid="login-password" placeholder="••••••••" className="pr-10" />
          <button type="button" onClick={() => setShow((v) => !v)} data-testid="login-password-toggle"
            aria-label={show ? t("Masquer le mot de passe") : t("Afficher le mot de passe")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button type="button" onClick={onForgot} className="mt-1.5 text-xs text-[#9E7B41] hover:underline" data-testid="forgot-link">
          {t("Mot de passe oublié ?")}
        </button>
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Se connecter")}
      </Button>
      <p className="text-xs text-muted-foreground text-center pt-1">{t("Besoin d'un accès revendeur ? Contactez votre administrateur Fautras.")}</p>
    </form>
  );
};

export default function Login() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [forgot, setForgot] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col lg:flex-row">
      <div className="relative lg:w-1/2 min-h-[240px] lg:min-h-screen overflow-hidden">
        <img src="/assets/van-hero.jpg" alt="Van Fautras" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 h-full flex flex-col justify-between p-8 lg:p-12">
          <div className="bg-white/95 rounded-lg px-3 py-2 inline-flex w-fit">
            <img src="/assets/fautras-logo.png" alt="Fautras" className="h-12 w-auto" />
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-3xl lg:text-4xl font-extrabold leading-tight text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]">
              {t("Configurateur de devis revendeurs")}
            </h1>
            <p className="mt-3 text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.7)]">
              {t("Vans chevaux & Fourgons Pro & Sport. Configurez, chiffrez et envoyez vos devis en quelques clics.")}
            </p>
          </div>
        </div>
      </div>
      <div className="lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4"><LoginLangPicker /></div>
          <div className="mb-6">
            <h2 className="font-display text-2xl font-bold">{forgot ? t("Mot de passe oublié") : t("Connexion")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("Accès revendeurs & administrateurs")}</p>
          </div>
          {forgot ? <ForgotForm initialEmail={email} onBack={() => setForgot(false)} />
            : <LoginForm email={email} setEmail={setEmail} onForgot={() => setForgot(true)} />}
        </div>
      </div>
    </div>
  );
}
